import { useCallback, useMemo, useState } from "react";
import { type TabData } from "@utils/dashboard-storage";
import { createGroup, validateGroupName } from "./group-utils";

/**
 * Owns the "Add Group" sub-flow: pick >=2 tabs from anywhere in the
 * dashboard, name the group, preview the resulting order, then confirm as
 * one atomic structural change.
 */
export const useGroupCreation = (
  localTabs: TabData[],
  setLocalTabs: (tabs: TabData[]) => void,
  onCreated: (nextTabs: TabData[], previousTabs: TabData[]) => void,
) => {
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [selectedTabIds, setSelectedTabIds] = useState<Set<string>>(new Set());

  const open = useCallback(() => {
    setIsOpen(true);
    setName("");
    setNameError(null);
    setSelectedTabIds(new Set());
  }, []);

  const close = useCallback(() => setIsOpen(false), []);

  const setNameValue = useCallback(
    (value: string) => {
      setName(value);
      setNameError(value ? validateGroupName(value, localTabs) : null);
    },
    [localTabs],
  );

  const toggleTab = useCallback((tabId: string) => {
    setSelectedTabIds((prev) => {
      const next = new Set(prev);
      if (next.has(tabId)) next.delete(tabId);
      else next.add(tabId);
      return next;
    });
  }, []);

  const canConfirm = !nameError && name.trim().length > 0 && selectedTabIds.size >= 2;

  const previewTabs = useMemo(() => {
    if (selectedTabIds.size === 0) return [];
    return createGroup(localTabs, Array.from(selectedTabIds), name || "New Group").filter((t) =>
      selectedTabIds.has(t.id),
    );
  }, [localTabs, selectedTabIds, name]);

  const confirm = useCallback(() => {
    const err = validateGroupName(name, localTabs);
    if (err) {
      setNameError(err);
      return;
    }
    if (selectedTabIds.size < 2) return;

    const before = localTabs;
    const next = createGroup(before, Array.from(selectedTabIds), name);
    setLocalTabs(next);
    setIsOpen(false);
    onCreated(next, before);
  }, [name, localTabs, selectedTabIds, setLocalTabs, onCreated]);

  return {
    isOpen,
    name,
    nameError,
    selectedTabIds,
    canConfirm,
    previewTabs,
    open,
    close,
    setName: setNameValue,
    toggleTab,
    confirm,
  } as const;
};
